/**
 * Pure function that generates a complete HTML document for pixel-perfect
 * article preview, matching the novela-share rendering exactly.
 *
 * The generated HTML is rendered inside a sandboxed <iframe> via srcDoc,
 * ensuring complete CSS isolation from the admin app's Tailwind styles.
 */

import {
  SHARE_PREVIEW_CSS,
  GOOGLE_FONTS_URL,
  PRETENDARD_URL,
  CUSTOM_FONT_FACES,
} from "./share-preview-styles";

// ─── Types ───

export type PreviewMode = "public" | "free" | "pro";

export interface PreviewSocialAccount {
  platformName: string;
  link: string;
}

export interface PreviewData {
  mode: PreviewMode;
  title: string;
  authorName: string;
  authorProfileImageURL?: string;
  authorBiography?: string;
  authorSocialAccounts?: PreviewSocialAccount[];
  thumbnailUrl?: string;
  thumbnailAlt?: string;
  freeContentHtml: string;
  fullContentHtml: string;
  publishedAt?: string;
}

// ─── Helpers ───

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function formatRelativeTime(dateString?: string): string {
  if (!dateString) return "방금 전";

  const now = new Date();
  const date = new Date(dateString);
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / 60_000);
  const diffHours = Math.floor(diffMs / 3_600_000);
  const diffDays = Math.floor(diffMs / 86_400_000);

  if (diffMinutes < 1) return "방금 전";
  if (diffMinutes < 60) return `${diffMinutes}분 전`;
  if (diffHours < 24) return `${diffHours}시간 전`;
  if (diffDays < 7) return `${diffDays}일 전`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}주 전`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)}개월 전`;
  return `${Math.floor(diffDays / 365)}년 전`;
}

// ─── Social Icon SVGs ───

const SOCIAL_ICONS: Record<string, string> = {
  x: `<svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M15.27 2.5h2.84l-6.2 7.08L19 17.5h-5.71l-4.47-5.85-5.12 5.85H.86l6.63-7.57L.5 2.5h5.86l4.05 5.36L15.27 2.5Zm-1 13.48h1.57L5.78 4.1H4.1l10.17 11.88Z" fill="currentColor"/></svg>`,
  instagram: `<svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10 6.67a3.33 3.33 0 1 0 0 6.66 3.33 3.33 0 0 0 0-6.66Zm0 5.5a2.17 2.17 0 1 1 0-4.34 2.17 2.17 0 0 1 0 4.34Z" fill="currentColor"/><circle cx="13.5" cy="6.5" r=".83" fill="currentColor"/><path d="M14.17 2.5H5.83A3.33 3.33 0 0 0 2.5 5.83v8.34a3.33 3.33 0 0 0 3.33 3.33h8.34a3.33 3.33 0 0 0 3.33-3.33V5.83a3.33 3.33 0 0 0-3.33-3.33Zm2.16 11.67a2.17 2.17 0 0 1-2.16 2.16H5.83a2.17 2.17 0 0 1-2.16-2.16V5.83a2.17 2.17 0 0 1 2.16-2.16h8.34a2.17 2.17 0 0 1 2.16 2.16v8.34Z" fill="currentColor"/></svg>`,
  homepage: `<svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M17.5 9.31 10.44 2.8a.63.63 0 0 0-.88 0L2.5 9.31a.62.62 0 0 0 .44 1.07h1.25v6.25c0 .34.28.62.63.62h3.75v-4.37h2.86v4.37h3.75c.35 0 .63-.28.63-.63v-6.25h1.25a.62.62 0 0 0 .44-1.06Z" fill="currentColor"/></svg>`,
  facebook: `<svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M17.5 10a7.5 7.5 0 1 0-8.67 7.41v-5.24H6.95V10h1.88V8.28c0-1.85 1.1-2.88 2.8-2.88.8 0 1.65.15 1.65.15v1.82h-.93c-.91 0-1.2.57-1.2 1.15V10h2.05l-.33 2.17h-1.72v5.24A7.5 7.5 0 0 0 17.5 10Z" fill="currentColor"/></svg>`,
  linkedin: `<svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M15.83 2.5H4.17C3.25 2.5 2.5 3.25 2.5 4.17v11.66c0 .92.75 1.67 1.67 1.67h11.66c.92 0 1.67-.75 1.67-1.67V4.17c0-.92-.75-1.67-1.67-1.67ZM6.67 15.83H4.17V8.33h2.5v7.5ZM5.42 7.25a1.46 1.46 0 1 1 0-2.92 1.46 1.46 0 0 1 0 2.92Zm10.41 8.58h-2.5v-3.66c0-.87-.01-2-1.22-2-1.22 0-1.41.96-1.41 1.94v3.72h-2.5V8.33h2.4v1.03h.03c.33-.63 1.15-1.3 2.37-1.3 2.53 0 3 1.67 3 3.84v3.93Z" fill="currentColor"/></svg>`,
  mail: `<svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M16.67 3.33H3.33c-.91 0-1.66.75-1.66 1.67v10c0 .92.75 1.67 1.66 1.67h13.34c.91 0 1.66-.75 1.66-1.67V5c0-.92-.75-1.67-1.66-1.67Zm-.24 3.54L10 10.63 3.57 6.87a.83.83 0 0 1 .86-1.42l5.57 3.4 5.57-3.4a.83.83 0 0 1 .86 1.42Z" fill="currentColor"/></svg>`,
};

function buildSocialIconHtml(platformName: string): string {
  const key = platformName.toLowerCase().trim();
  const svg = SOCIAL_ICONS[key];
  if (!svg) return "";
  return `<span class="social-icon">${svg}</span>`;
}

// ─── Section Builders ───

function buildHeaderHtml(title: string): string {
  return `
  <header class="article-header">
    <span class="article-header__title">${escapeHtml(title)}</span>
    <div></div>
  </header>`;
}

function buildThumbnailHtml(url?: string, alt?: string): string {
  if (!url) return "";
  return `
    <div class="thumbnail-container">
      <img
        src="${escapeHtml(url)}"
        alt="${escapeHtml(alt || "")}"
        class="thumbnail"
        width="320"
        height="180"
      />
    </div>`;
}

function buildContentHeaderHtml(data: PreviewData): string {
  const relativeTime = formatRelativeTime(data.publishedAt);
  return `
    <div class="content-header">
      <h1 class="article-title">${escapeHtml(data.title)}</h1>
      <div class="content-meta">
        <span class="typo-title2-medium text-secondary">${escapeHtml(data.authorName)}</span>
        <span class="meta-separator">&middot;</span>
        <span class="typo-body2-regular text-tertiary">${escapeHtml(relativeTime)}</span>
      </div>
    </div>`;
}

function buildContentViewerHtml(contentHtml: string): string {
  return `<div class="article-content-viewer">${contentHtml}</div>`;
}

function buildFreePaywallOverlayHtml(): string {
  return `
    <div class="paywall-overlay">
      <div class="paywall-blur"></div>
      <div class="paywall-content">
        <div class="typo-title1-bold text-primary-500 paywall-heading">
          다음 내용이 궁금하신가요?
        </div>
        <div class="paywall-description">
          <span class="typo-body1-medium text-tertiary">
            Pro 요금제(월 8,250원)로 프리미엄 아티클뿐만 아니라,
          </span>
          <span class="typo-body1-medium text-tertiary">
            노벨라의 모든 기능을 제한 없이 누려보세요.
          </span>
        </div>
        <div class="paywall-buttons">
          <span class="paywall-btn-subscribe">
            <span class="typo-title3-semibold text-white">노벨라 구독하고 계속 읽기</span>
          </span>
        </div>
      </div>
    </div>`;
}

function buildPublicPaywallOverlayHtml(): string {
  return `
    <div class="paywall-overlay">
      <div class="paywall-blur"></div>
      <div class="paywall-content">
        <div class="typo-title1-bold text-primary-500 paywall-heading">
          다음 내용이 궁금하신가요?
        </div>
        <div class="paywall-description">
          <span class="typo-body1-medium text-tertiary">
            Pro 요금제(월 8,250원)로 프리미엄 아티클뿐만 아니라,
          </span>
          <span class="typo-body1-medium text-tertiary">
            노벨라의 모든 기능을 제한 없이 누려보세요.
          </span>
        </div>
        <div class="paywall-buttons paywall-buttons--public">
          <span class="paywall-btn-login">
            <span class="typo-title3-semibold text-neutral-50">가입 및 로그인</span>
          </span>
          <span class="paywall-btn-subscribe">
            <span class="typo-title3-semibold text-white">노벨라 구독하고 계속 읽기</span>
          </span>
        </div>
      </div>
    </div>`;
}

function buildContentSectionHtml(data: PreviewData): string {
  const isPaywalled = data.mode === "public" || data.mode === "free";
  const content = isPaywalled ? data.freeContentHtml : data.fullContentHtml;

  const containerClass =
    data.mode === "public"
      ? "content-container--public"
      : data.mode === "free"
        ? "content-container--with-overlay"
        : "content-container";

  const overlay =
    data.mode === "public"
      ? buildPublicPaywallOverlayHtml()
      : data.mode === "free"
        ? buildFreePaywallOverlayHtml()
        : "";

  return `
    <div class="${containerClass}">
      ${buildContentViewerHtml(content)}
      ${overlay}
    </div>`;
}

function buildProfileImageHtml(imageUrl?: string): string {
  if (!imageUrl) {
    return `
      <div class="profile-image">
        <svg class="profile-icon-placeholder" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
        </svg>
      </div>`;
  }

  return `
    <div class="profile-image">
      <img src="${escapeHtml(imageUrl)}" alt="profile image" width="58" height="58" />
    </div>`;
}

function buildSocialAccountsHtml(accounts?: PreviewSocialAccount[]): string {
  if (!accounts || accounts.length === 0) return "";

  const icons = accounts
    .map((account) => {
      const iconHtml = buildSocialIconHtml(account.platformName);
      if (!iconHtml) return "";
      return `<a class="social-link" href="${escapeHtml(account.link)}" target="_blank" rel="noopener noreferrer">${iconHtml}</a>`;
    })
    .filter(Boolean)
    .join("");

  if (!icons) return "";

  return `
    <div class="profile-divider"></div>
    <div class="social-icons">${icons}</div>`;
}

function buildAuthorProfileHtml(data: PreviewData): string {
  if (!data.authorName) return "";

  const biography = data.authorBiography
    ? `<p class="typo-body1-regular text-primary">${escapeHtml(data.authorBiography)}</p>`
    : "";

  return `
    <div class="author-profile-container">
      <div class="author-profile">
        ${buildProfileImageHtml(data.authorProfileImageURL)}
        <div class="profile-content">
          <div class="profile-name-row">
            <span class="typo-h4-semibold text-primary">${escapeHtml(data.authorName)}</span>
            ${buildSocialAccountsHtml(data.authorSocialAccounts)}
          </div>
          ${biography}
        </div>
      </div>
    </div>`;
}

function buildCopyrightHtml(): string {
  return `
    <div class="copyright-container">
      <span class="copyright-text">Created with Novela</span>
    </div>`;
}

// ─── Main Builder ───

export function buildArticleBody(data: PreviewData): string {
  return `
    ${buildHeaderHtml(data.title)}
    <main class="article-layout">
      ${buildThumbnailHtml(data.thumbnailUrl, data.thumbnailAlt)}
      ${buildContentHeaderHtml(data)}
      ${buildContentSectionHtml(data)}
      ${buildAuthorProfileHtml(data)}
      ${buildCopyrightHtml()}
    </main>`;
}

export function buildPreviewShellHtml(): string {
  return `<!DOCTYPE html>
<html lang="ko">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link rel="stylesheet" href="${GOOGLE_FONTS_URL}">
  <link rel="stylesheet" href="${PRETENDARD_URL}">
  <style>
    ${CUSTOM_FONT_FACES}
    ${SHARE_PREVIEW_CSS}
  </style>
</head>
<body></body>
</html>`;
}

export function buildPreviewHtml(data: PreviewData): string {
  return `<!DOCTYPE html>
<html lang="ko">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link rel="stylesheet" href="${GOOGLE_FONTS_URL}">
  <link rel="stylesheet" href="${PRETENDARD_URL}">
  <style>
    ${CUSTOM_FONT_FACES}
    ${SHARE_PREVIEW_CSS}
  </style>
</head>
<body>
  ${buildArticleBody(data)}
</body>
</html>`;
}
